import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { sha256 } from '@/shared/util/formatters';
import type {
  IAddChildRiderPayload,
  IAuditLog,
  IBootstrap,
  ICustodialAccountMeta,
  IGetBankDataPayload,
  IGetBeneficiariesResponse,
  IGetPaymentInfoResponse,
  IPolicyOverview,
  ISearchCustodialAccountPayload,
} from './models.bff';

import type {
  ICustomerStoreInvestments,
  ICustomerStoreInvestmentsGroupAccount,
  ICustomerStoreInvestmentsCustodian,
} from '../../stores/customer/customerStore.model';

// * GET /alerts
const getAlerts = async () => {
  const response: AxiosResponse = await axios.get(`${window.appConfig.endpoints.bff}/alerts`);
  return response;
};

// * PUT /alerts/${alertId}/dismiss
const dismissAlert = async (alertId: string) => {
  const response: AxiosResponse = await axios.put(
    `${window.appConfig.endpoints.bff}/alerts/${alertId}/dismiss`
  );
  return response;
};

// * PUT /alerts/${alertId}/complete
const completeAlert = async (alertId: string, payload: object) => {
  const response: AxiosResponse = await axios.put(
    `${window.appConfig.endpoints.bff}/alerts/${alertId}/complete`,
    payload
  );
  return response;
};

// * GET /documents${query}
const getDocuments = async (policyIds: string[] | undefined) => {
  const query = policyIds ? `?policyIds=${policyIds}` : '';
  const response: AxiosResponse = await axios.get(
    `${window.appConfig.endpoints.bff}/documents${query}`
  );
  return response;
};

// * GET /pub/info
const getAppInfo = async () => {
  const response: AxiosResponse = await axios.get(`${window.appConfig.endpoints.bff}/pub/info`);
  return response;
};

// * GET /pub/auditlog
const postAuditLog = async (auditlog: IAuditLog) => {
  const response: AxiosResponse = await axios.post(
    `${window.appConfig.endpoints.bff}/pub/auditlog`,
    auditlog
  );
  return response.data;
};

// * POST /policies/${policyNumber}/reinstatement
const postPolicyReinstatement = async (payload: {
  policyNumber: string;
  reinstatementPayload: object;
}) => {
  const { policyNumber, reinstatementPayload } = payload;
  const response: AxiosResponse = await axios.post(
    `${window.appConfig.endpoints.bff}/policies/${policyNumber}/reinstatement`,
    reinstatementPayload
  );
  return response;
};

// * GET /investments/custodians
const searchMinorAccount = async (
  payload: ISearchCustodialAccountPayload
): Promise<ICustodialAccountMeta[]> => {
  const { taxId, dob } = payload;
  const hashTaxId = await sha256(taxId); // Convert taxId to SHA256 Hash
  const query = `type=custodian&ssnHashed=${hashTaxId.toUpperCase()}&dob=${dob}`;
  const response: AxiosResponse = await axios.get(
    `${window.appConfig.endpoints.bff}/investments?${query}`
  );
  const responseData: ICustomerStoreInvestments = response.data;
  if (responseData.groups === undefined || responseData.groups.length === 0) {
    return [];
  }
  const custodianAccount = responseData.groups[0]?.accounts;
  return (
    custodianAccount?.map(
      (account: ICustomerStoreInvestmentsGroupAccount): ICustomerStoreInvestmentsCustodian => ({
        accountId: account.id,
        accountNumber: account.accountNumber,
        totalMarketValue: account.endingValue,
        lastName: account.lastName,
        firstName: account.firstName,
      })
    ) ?? []
  );
};

// * PUT /investments/custodians/:id/link
const linkMinorAccount = async (accountId: string) => {
  // ? ex. https://gtwd.primericaonline.com/dev/pc2/bff/api/investments/custodians/2456636/link
  const linkContract = {
    contractId: accountId.toString(),
    type: 'sec',
    role: 'custodian',
  };
  const response: AxiosResponse = await axios.post(
    `${window.appConfig.endpoints.bff}/customers/link-contracts`,
    linkContract
  );
  return response.status === 204;
};

// Investment Account Number
const getAccountNumber = async (accountId: string) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const response: AxiosResponse = await axios.get(
      `${window.appConfig.endpoints.bff}/investments/${accountId}?include=numberEncrypted`
    );
    if (response.status === 200 && response.data) {
      return response.data.numberEncrypted;
    }
  } catch (e) {
    throw e;
  }
  return '';
};

const bootstrap = async (): Promise<IBootstrap> => {
  const response: AxiosResponse<IBootstrap> = await axios.get(
    `${window.appConfig.endpoints.bff}/bootstrap`
  );
  return response.data;
};

const getInvestments = async (
  country: string,
  features: string
): Promise<ICustomerStoreInvestments> => {
  const query = !features ? `?country=${country}` : `?country=${country}&features=${features}`;
  const response: AxiosResponse = await axios.get(
    `${window.appConfig.endpoints.bff}/investments${query}`
  );
  return response.data as ICustomerStoreInvestments;
};

const getCommunicationsPreferences = async () => {
  const url = `${window.appConfig.endpoints.bff}/communications/preferences`;
  const response: AxiosResponse = await axios.get(url);
  return response.data.life;
};

const getInvestmentsCommunicationsPreferences = async () => {
  // uncomment all commented code
  const url = `${window.appConfig.endpoints.bff}/communications/preferences`;
  const response = await axios.get(url);
  return response.data?.investment;
  // return { channel: 'MAIL' };
};

// this one is missing source param that is there in vue1
const updatePaperless = async (
  selections: { [key: string]: string },
  locale: string,
  setEcommDisplayed = false,
  source?: string
) => {
  const settings = Object.keys(selections).map((key) => ({
    policyNumber: key,
    channel: selections[key],
  }));
  const options: AxiosRequestConfig = {};
  if (setEcommDisplayed) {
    options.headers = { setEcommDisplayed: 'true', preferredLang: locale };
  } else {
    options.headers = { preferredLang: locale };
  }
  const payload = {
    life: settings,
    source,
  };
  return axios.patch(
    `${window.appConfig.endpoints.bff}/communications/preferences`,
    payload,
    options
  );
};

const updateInvestmentPaperless = async (payload: unknown, locale: string) => {
  // const settings = Object.keys(selections).map(key => ({
  //   policyNumber: key,
  //   channel: selections[key],
  // }));
  // const options = {};
  // if (setEcommDisplayed) {
  //   options.headers = { setEcommDisplayed: 'true', preferredLang: locale };
  // } else {
  //   options.headers = { preferredLang: locale };
  // }
  const options: AxiosRequestConfig = {};
  options.headers = { preferredLang: locale };

  return axios.patch(
    `${window.appConfig.endpoints.bff}/communications/preferences`,
    payload,
    options
  );
};

const snoozeEcomm = async () => {
  return axios.patch(`${window.appConfig.endpoints.bff}/communications/snooze?type=ecomm`);
};

const snoozeVerifyPhone = async () => {
  return axios.patch(`${window.appConfig.endpoints.bff}/communications/snooze?type=phone`);
};

const getInvestmentStatementMetadata = async () => {
  const response: AxiosResponse = await axios.get(
    `${window.appConfig.endpoints.bff}/investments/statements`
  );
  return response.data;
};

const getInvestmentCalculator = async (scenario: object) => {
  const response: AxiosResponse = await axios.post(
    `${window.appConfig.endpoints.bff}/investments/calculator`,
    scenario
  );
  return response.data;
};

const getSamlToken = async (scenario: object) => {
  const response: AxiosResponse = await axios.post(
    `${window.appConfig.endpoints.bff}/investments/samltoken`,
    scenario
  );
  return response.data;
};

const getPolicyOverview = async (policyIds?: string[]): Promise<IPolicyOverview> => {
  const query = policyIds ? `?policyIds=${policyIds}` : '';
  const response = await axios.get(`${window.appConfig.endpoints.bff}/policies${query}`);
  return response.data;
};

const getDimeData = async () => {
  return axios.get(`${window.appConfig.endpoints.bff}/dime`);
};

const dimeCalc = async (dimeInput: object) => {
  const res = await axios.put(`${window.appConfig.endpoints.bff}/dime/calculation`, dimeInput);
  return res.data;
};

const saveDimeData = async (data: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/dime`, data);
};

const getDebt = async () => {
  return axios.get(`${window.appConfig.endpoints.bff}/debt/scenarios`);
};

const postDebtAndCalculate = async (scenario: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/debt/postdebt`, scenario);
};

const updateContactInfo = async (info: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/contactInfo`, info);
};

const updateMobileNumber = async (mobileInfo: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/customers/phone`, mobileInfo);
};

const verifyContactInfo = async (info: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/contactInfo/verify`, info);
};

const sendRepEmail = async (email: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/contact/agent`, email);
};

const addChildToRider = async (childRider: IAddChildRiderPayload) => {
  return axios.post(`${window.appConfig.endpoints.bff}/policies/child/add`, childRider);
};

const getPaymentInfo = async (number: string): Promise<IGetPaymentInfoResponse> => {
  const response = await axios.get(`${window.appConfig.endpoints.bff}/policies/payment`, {
    headers: {
      policyNumber: number,
    },
  });
  if (response.status === 200 || response.status === 201 || response.status === 204) {
    return response.data;
  } else {
    throw Error('not 200');
  }
};

const getBankData = async (data: IGetBankDataPayload): Promise<unknown> => {
  const response = await axios.get(`${window.appConfig.endpoints.bff}/policies/payment/info`, {
    headers: {
      policyNo: data.policyNo,
      routingNumber: data.routingNumber,
      country: data.country,
    },
  });
  return response.data;
};

const submitAchPayment = async (data: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/payment/submit`, data);
};

const updateBillingMode = async (data: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/payment/billingmode/update`, data);
};

const getDraftDescription = async (data: {
  paidToDate: string;
  selectedMonthlyPaymentDay: string;
}) => {
  return axios.get(`${window.appConfig.endpoints.bff}/policies/draft/description`, {
    headers: {
      paidtodate: data.paidToDate,
      selectedday: data.selectedMonthlyPaymentDay,
    },
  });
};

const submitEcomms = async (data: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/policies/ecomm/update`, data);
};

const pullPackages = async () => {
  return axios.get(`${window.appConfig.endpoints.bff}/crc/packages/summary`);
};

const pullPackage = async (policyId: string, packageId: string) => {
  return axios.get(`${window.appConfig.endpoints.bff}/crc/package/info`, {
    headers: {
      packageId,
      policyId,
    },
  });
};

const updatePackage = async (data: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/crc/package/submit`, data);
};

const getAgreement = async (type = 'U', locale: string, policyNumber: string) => {
  return axios.get(
    `${window.appConfig.endpoints.bff}/customers/agreements/get?policyNumber=${policyNumber}&locale=${locale}&type=${type}`
  );
};

const updateLanguagePreference = async (preferredLang: string) => {
  return axios.patch(`${window.appConfig.endpoints.bff}/customers/preferredlang`, {
    preferredLang,
  });
};

const paymentSelection = async (data: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/policies/paymentselection`, data);
};

const updatePassword = async (request: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/customers/password_change`, request);
};

const changeUserVerification = async (request: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/customers/id/change-verification`, request);
};

const createChallenge = async (challengeSpec: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/customers/challenges`, challengeSpec);
};

const requestUserIdChallenge = async (request: object) => {
  return axios.post(`${window.appConfig.endpoints.bff}/customers/challenges`, request);
};

const updateUserID = async (request: object) => {
  return axios.patch(`${window.appConfig.endpoints.bff}/customers/id`, request);
};

const getAgents = async (ids: string[]) => {
  const response: AxiosResponse = await axios.get(
    `${window.appConfig.endpoints.bff}/agents/${ids.join(',')}`
  );
  return response.data;
};

const getTrustedDevices = async () => {
  return axios.get(`${window.appConfig.endpoints.bff}/devices/trusted-devices`);
};

const deleteTrustedDevices = async () => {
  return axios.delete(`${window.appConfig.endpoints.bff}/devices/trusted-devices`);
};

const ackPolicy = async (data: { policyNum: string; locale: string }) => {
  return axios.patch(`${window.appConfig.endpoints.bff}/policies/acknowledge`, data);
};

const timeout = async (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const getRightAnswersHelpUrl = async () => {
  const rightanswerslink = window.appConfig.endpoints.rightanswerslink;
  const x = axios.get(rightanswerslink);
  await timeout(2500);
  return x;
};

const getBeneficiaries = async (policyNumber: string): Promise<IGetBeneficiariesResponse[]> => {
  const response = await axios.get(
    `${window.appConfig.endpoints.bff}/policies/${policyNumber}/beneficiaries`
  );
  return response.data;
};

const setBeneficiaries = async (policyNumber: string, data: object) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/${policyNumber}/beneficiaries`, {
    beneficiaries: data,
  });
};

const setMonthlyWithDrawalDay = async (policyNumber: string, data: string) => {
  return axios.put(`${window.appConfig.endpoints.bff}/policies/${policyNumber}/payment/draftday`, {
    draftDay: data,
  });
};

const updateTermsOfUse = async (agreementId: string) => {
  return axios.patch(
    `${window.appConfig.endpoints.bff}/customers/agreements/${agreementId}/version`
  );
};

export default {
  bootstrap,
  getInvestments,
  getCommunicationsPreferences,
  getInvestmentsCommunicationsPreferences,
  updatePaperless,
  snoozeEcomm,
  snoozeVerifyPhone,
  getInvestmentStatementMetadata,
  getInvestmentCalculator,
  getSamlToken,
  getPolicyOverview,
  getDimeData,
  dimeCalc,
  saveDimeData,
  getDebt,
  postDebtAndCalculate,
  updateContactInfo,
  updateMobileNumber,
  verifyContactInfo,
  sendRepEmail,
  addChildToRider,
  getPaymentInfo,
  getBankData,
  submitAchPayment,
  updateBillingMode,
  getDraftDescription,
  submitEcomms,
  pullPackages,
  pullPackage,
  updatePackage,
  getAgreement,
  updateLanguagePreference,
  paymentSelection,
  updatePassword,
  changeUserVerification,
  createChallenge,
  requestUserIdChallenge,
  updateUserID,
  getAgents,
  getTrustedDevices,
  deleteTrustedDevices,
  ackPolicy,
  getRightAnswersHelpUrl,
  timeout,
  getBeneficiaries,
  setBeneficiaries,
  setMonthlyWithDrawalDay,
  updateTermsOfUse,
  getAlerts,
  dismissAlert,
  completeAlert,
  getDocuments,
  getAppInfo,
  postAuditLog,
  postPolicyReinstatement,
  searchMinorAccount,
  linkMinorAccount,
  getAccountNumber,
  updateInvestmentPaperless,
};
